/**
 * Horizontal price bands for the Planet Retrograde study.
 *
 * The study already highlights each retrograde period as a vertical band. These bands answer the
 * follow-on question: once the planet turned direct, did price stay inside the range it made while
 * retrograde? Each band takes the high/low of the retrograde window and extends that range forward
 * to the start of the next retrograde, so the price action between cycles is read against it.
 *
 *   E_n retrograde window            band drawn from the END of E_n
 *   ┌───────────┐                    to the START of E_n+1
 *   │  Y_max ───┼────────────────────────────────────┐
 *   │           │                                    │
 *   │  Y_min ───┼────────────────────────────────────┘
 *   └───────────┘                                   E_n+1
 *      X of E_n        X1 = end of E_n     X2 = start of E_n+1
 *
 * Deliberately scoped to this study. It is exported as a separate helper and called from one
 * branch of the overlay builder, so no other area-highlight study picks it up by accident.
 */

/**
 * OHLC column layout, matching how App.js builds barData:
 *   ohlc: sortedData.map(item => [item.open, item.close, item.low, item.high])
 *
 * That is ECharts' candlestick order, NOT the conventional open/high/low/close — low is index 2
 * and high is index 3. Reading it as OHLC gives the close where the low belongs, which produces
 * bands that look plausible and are quietly wrong.
 */
export const OHLC_OPEN = 0;
export const OHLC_CLOSE = 1;
export const OHLC_LOW = 2;
export const OHLC_HIGH = 3;

/**
 * Fill and stroke per planet. Fill sits at 15% so candles stay readable through it; the stroke is
 * the same hue at 80% so a band's edges are findable when several overlap.
 *
 * <p>Mercury is the exception, at 28% fill and a fully opaque stroke. It is the retrograde study
 * anyone actually runs — three cycles a year against Mars's two-yearly — so its bands are the ones
 * read closely, and amber is the palest hue here: at 15% on a white chart it was barely present.
 * A deeper amber on a hue this light still leaves the candles legible through it, which is the
 * constraint the 15% default exists to satisfy.
 */
export const PLANET_BAND_COLORS = {
  Venus:   { fill: 'rgba(76, 175, 80, 0.15)',  stroke: 'rgba(76, 175, 80, 0.8)' },   // light green
  Mercury: { fill: 'rgba(230, 126, 0, 0.28)',  stroke: 'rgba(191, 96, 0, 1)' },      // amber, deepened
  Mars:    { fill: 'rgba(244, 67, 54, 0.15)',  stroke: 'rgba(244, 67, 54, 0.8)' },   // red
  Jupiter: { fill: 'rgba(156, 39, 176, 0.15)', stroke: 'rgba(156, 39, 176, 0.8)' },  // purple
  Saturn:  { fill: 'rgba(0, 150, 136, 0.15)',  stroke: 'rgba(0, 150, 136, 0.8)' },   // teal
  // Remaining bodies, kept far apart in hue from the five above and from each other.
  Uranus:  { fill: 'rgba(3, 169, 244, 0.15)',  stroke: 'rgba(3, 169, 244, 0.8)' },   // light blue
  Neptune: { fill: 'rgba(63, 81, 181, 0.15)',  stroke: 'rgba(63, 81, 181, 0.8)' },   // indigo
  Pluto:   { fill: 'rgba(121, 85, 72, 0.15)',  stroke: 'rgba(121, 85, 72, 0.8)' },   // brown
  Rahu:    { fill: 'rgba(233, 30, 99, 0.15)',  stroke: 'rgba(233, 30, 99, 0.8)' },   // pink
  Ketu:    { fill: 'rgba(96, 125, 139, 0.15)', stroke: 'rgba(96, 125, 139, 0.8)' },  // blue grey
  Sun:     { fill: 'rgba(255, 193, 7, 0.15)',  stroke: 'rgba(255, 193, 7, 0.8)' },   // gold
  Moon:    { fill: 'rgba(158, 158, 158, 0.15)', stroke: 'rgba(158, 158, 158, 0.8)' }, // grey
};

/** Used when a planet is unrecognised — visible, but clearly not one of the mapped hues. */
export const DEFAULT_BAND_COLOR = { fill: 'rgba(120, 144, 156, 0.15)', stroke: 'rgba(120, 144, 156, 0.8)' };

/** Colours for a planet, case- and whitespace-insensitively. */
export function bandColorsForPlanet(planet) {
  if (!planet) return DEFAULT_BAND_COLOR;
  const key = String(planet).trim().toLowerCase();
  const match = Object.keys(PLANET_BAND_COLORS).find(p => p.toLowerCase() === key);
  return match ? PLANET_BAND_COLORS[match] : DEFAULT_BAND_COLOR;
}

/**
 * Builds the band for each retrograde event.
 *
 * @param {Array<{startDate:string,endDate:string}>} events retrograde windows, already snapped to
 *        dates present in `dates`. Order is not assumed — they are sorted by position here.
 * @param {string[]} dates chart dates, ascending
 * @param {Array<number[]>} ohlc rows aligned with `dates`, in [open, close, low, high] order
 * @returns {Array<{date1:string,price1:number,date2:string,price2:number,eventIndex:number}>}
 */
export function computeRetrogradeBands(events, dates, ohlc) {
  if (!Array.isArray(events) || !Array.isArray(dates) || !Array.isArray(ohlc)) return [];
  if (dates.length === 0) return [];

  const indexOf = d => dates.indexOf(d);

  // Sort by where the window starts. Bands are defined by what comes NEXT, so an unordered input
  // would pair an event with the wrong successor and silently draw bands of the wrong length.
  const ordered = events
    .map(e => ({ ...e, startIdx: indexOf(e.startDate), endIdx: indexOf(e.endDate) }))
    .filter(e => e.startIdx !== -1 && e.endIdx !== -1)
    .sort((a, b) => a.startIdx - b.startIdx);

  const bands = [];

  // COVERAGE, reported rather than assumed.
  //
  // Bands tile from the FIRST event's start to the right edge, so the only structural gap is the
  // stretch before the first event - where no range has been established yet and drawing one would
  // assert a level that did not exist. That gap is real and is not a bug, but it is also the one
  // thing that looks identical to the decorations "dropping off", so it is stated.
  //
  // Anything ELSE missing means events were discarded above, and the count says how many.
  const discarded = events.length - ordered.length;
  if (discarded > 0) {
    console.warn(`[bands] ${discarded} of ${events.length} event(s) had no matching bar on the `
      + 'axis and were dropped - their stretch of the chart will have no horizontal band');
  }
  if (ordered.length > 0 && ordered[0].startIdx > 0) {
    console.debug(`[bands] no band before ${dates[ordered[0].startIdx]} `
      + `(${ordered[0].startIdx} bars): no range is established until the first event`);
  }

  ordered.forEach((event, i) => {
    const lo = Math.min(event.startIdx, event.endIdx);
    const hi = Math.max(event.startIdx, event.endIdx);

    // Price bounds from the bars inside the retrograde window, inclusive of its first and last
    // bar: those are the bars the vertical highlight visibly covers. Excluding them would make a
    // one-bar retrograde produce no band at all.
    let yMin = Infinity;
    let yMax = -Infinity;
    for (let k = lo; k <= hi; k++) {
      const bar = ohlc[k];
      if (!bar) continue;
      const low = bar[OHLC_LOW];
      const high = bar[OHLC_HIGH];
      if (typeof low === 'number' && Number.isFinite(low)) yMin = Math.min(yMin, low);
      if (typeof high === 'number' && Number.isFinite(high)) yMax = Math.max(yMax, high);
    }
    // A window of padding bars (nulls) has no range to project forward.
    if (!Number.isFinite(yMin) || !Number.isFinite(yMax)) return;

    // X1 is where this retrograde BEGINS — the left edge of the vertical highlight, not its right
    // edge. The band therefore covers the retrograde period itself and then continues to the next
    // one, so the high/low is drawn across the very bars that produced it. Starting at the right
    // edge left the range floating beside the window it came from, with nothing tying the two
    // together visually.
    //
    // X2 is where the next retrograde begins, or the last chart bar when this is the most recent
    // event — the band then runs to the right edge, which is the range still in force.
    const next = ordered[i + 1];
    const x2Idx = next ? next.startIdx : dates.length - 1;

    // Nothing to draw when there is no room to the right of the window's start: a zero-width band
    // renders as a bare vertical line that reads as a study marker rather than a range.
    if (x2Idx <= lo) return;

    bands.push({
      date1: dates[lo],
      price1: yMin,
      date2: dates[x2Idx],
      price2: yMax,
      eventIndex: i,
      isOpenEnded: !next,
    });
  });

  return bands;
}
